"use client";

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DataGridPro, GridColumnVisibilityModel, GridRowId, GridRowParams, GridRowProps, gridRowSelectionManagerSelector,
  GridSortModel, useGridApiRef,
} from '@mui/x-data-grid-pro';
import { GridRowScrollEndParams } from "@mui/x-data-grid-pro/models";
import { DndContext } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useAudioPlayback } from '@/common/hooks/useAudioPlayback.ts';
import useExplorerItems from '@/common/hooks/useExplorerItems.ts';
import { EventTypes, EXPLORER_DATAGRID_PAGESIZE, ExplorerItem, ExplorerItemId, ExplorerItems } from '@/types/types.ts';
import { URLSearchParamKeys } from '@/enum/URLSearchParamKeys.ts';
import { ExplorerItemType } from '@/enum/explorerItemType.ts';
import { Sortable } from './Sortable';
import { ExplorerItemRowTrack } from '@/features/Explorer/ExplorerItemRowTrack.tsx';
import { ExplorerItemRowPlaylist } from '@/features/Explorer/ExplorerItemRowPlaylist.tsx';
import useUserIsWriting from '@/common/hooks/useUserIsWriting.ts';
import { SelectoTracks } from '@/features/Selecto/SelectoTracks.tsx';
import useDataGridApi from '@/common/hooks/useDataGridApi.ts';
import DraggableBadge from '@/features/Explorer/DraggableBadge.tsx';
import useCurrentExplorerItemId from "@/common/hooks/useCurrentExplorerItemId.ts";
import { useMutateExplorerItems } from "@/state/jotai/hooks/useMutateExplorerItems.ts";
import useUniqueTabId from "@/common/hooks/useUniqueTabId.ts";
import { useSearchParamState } from "@/common/hooks/nuqs/useSearchParamState.ts";
import { renamePlaylist } from "@/services/api/playlist.ts";
import { useCellEditMode } from "@/common/hooks/useCellEditMode.ts";
import { EXPLORER_ROOT } from "@/features/Explorer/Explorer.tsx";
import { useSortableApi } from "@/features/Explorer/hooks/useSortableApi.ts";
import {
  DEFAULT_SORTING_MODEL, ExplorerContentColumns, explorerContentSlotProps
} from "@/features/Explorer/ExplorerContentColumns.tsx";
import { emitEvent, Emitter } from "@/services/event-emitter.ts";
import useSearchResults from "@/common/hooks/useSearchResults.ts";
import { fetchExplorerItems, fetchRootExplorerItems, fetchSearchExplorerItems } from "@/services/api/explorer-item.ts";
import { PlaymodeType } from "@/enum/playmodeType.ts";
import useLoadBreadcrumbsBySearchParam from "@/common/hooks/useLoadBreadcrumbsBySearchParam.ts";
import { DraggableOverlay } from "@/features/Explorer/DraggableOverlay.tsx";
import { ExplorerTools } from "@/features/Explorer/ExplorerTools.tsx";
import { renameTrack } from "@/services/api/track.ts";
import useMuiFillerContextMenu from "@/features/ContextMenu/hooks/useMuiFillerContextMenu.tsx";
import useExplorerDataGridColumnVisibilityModel
  from "@/common/hooks/preferences/useExplorerDataGridColumnVisibilityModel.ts";
import { createPortal } from "react-dom";
import { useMediaQuery } from "@mui/material";
import useExplorerDataGridSortingModel from "@/common/hooks/preferences/useExplorerDataGridSortingModel.ts";
import { FlexBox } from "@/common/components/Box/FlexBox.tsx";
import useHash from "@/common/hooks/useHash.ts";
import useExplorerBreadcrumbs from "@/common/hooks/useExplorerBreadcrumbs.ts";
import { Theme } from "@mui/material/styles";
import { useDataGridScrollRestoration } from "@/common/hooks/useDataGridScrollRestoration.ts";
import { isMobile } from "react-device-detect";
import { DroppableFolderUpButton } from "@/features/Explorer/DroppableFolderUpButtonProps.tsx";
import { useBandWorkspace } from "@/client/provider/band-workspace-context-provider.tsx";

const ExplorerToolsMemo = React.memo(ExplorerTools);
export const DRAGGING_LEFT_EDGE_THRESHOLD = -30;

export const ExplorerContent = () => {
  const apiRef = useGridApiRef();
  const currentHash = useHash();
  const userIsWriting = useUserIsWriting();
  const [uniqueTabId] = useUniqueTabId();
  const [searchResults, setSearchResults] = useSearchResults()
  const [explorerItems] = useExplorerItems();
  const [, setDataGridApi] = useDataGridApi();
  const { play, replay, togglePlay } = useAudioPlayback();
  const [isSearching, setIsSearching] = useState(false);
  // const [searchResults] = useSearchResults();
  const [explorerBreadcrumbs] = useExplorerBreadcrumbs();
  const [currentExplorerItemId] = useCurrentExplorerItemId()
  const isMediumScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [explorerDataGridSortingModel, setExplorerDataGridSortingModel] = useExplorerDataGridSortingModel();
  const [qSearchParam, setQSearchParam] = useSearchParamState(URLSearchParamKeys.Q);
  const [, setPlaymodeQueryString] = useSearchParamState(URLSearchParamKeys.PLAY_MODE);
  const isDefaultSorting = explorerDataGridSortingModel.length === 0 || explorerDataGridSortingModel === DEFAULT_SORTING_MODEL;
  const [playlistId, setPlaylistId] = useSearchParamState(URLSearchParamKeys.PLAYLIST, { defaultValue: EXPLORER_ROOT })
  const isInExplorer = useMemo(() => !currentHash || currentHash.includes('#explorer'), [currentHash]);
  const [explorerDataGridColumnVisibilityModel, setExplorerDataGridColumnVisibilityModel] = useExplorerDataGridColumnVisibilityModel();
  const { getInitialScrollState } = useDataGridScrollRestoration();
  const rowIds = useMemo(() => {
    return apiRef.current?.getAllRowIds() || []
  }, [
    apiRef,
  ]);
  useMuiFillerContextMenu();
  useLoadBreadcrumbsBySearchParam();
  const {
    updateItem,
    appendItems,
  } = useMutateExplorerItems();
  const bandWorkspace = useBandWorkspace();

  const visibleRows = useMemo(() => {
    const rows = explorerItems ? Object.values(explorerItems) : [];

    if (qSearchParam && searchResults) {
      return Object.values(searchResults)
    }

    return rows.filter(row => {
      if (row.deletedAt) {
        return false;
      }

      return !row.hidden
        && row.explorerItemParentId === playlistId;
    });
  }, [
    explorerItems,
    playlistId,
    qSearchParam
  ]);

  const handleRowsScrollEnd = useCallback((params: GridRowScrollEndParams) => {
    const { visibleRowsCount } = params;

    // If we have fewer visible rows than the limit, there's nothing more to load
    if (visibleRowsCount < EXPLORER_DATAGRID_PAGESIZE) {
      return;
    }

    // Get total number of rows currently loaded
    const totalRowCount = apiRef.current?.getRowsCount() || 0;

    // If the last fetch returned fewer items than the limit, we've reached the end
    if (totalRowCount % EXPLORER_DATAGRID_PAGESIZE !== 0) {
      // return;
    }

    // console.log('handleRowsScrollEnd')

    const visibleRowsCountRounded = Math.ceil(visibleRowsCount / EXPLORER_DATAGRID_PAGESIZE) * EXPLORER_DATAGRID_PAGESIZE;

    /*// Check if visibleRowsCount is an exact multiple of EXPLORER_DATAGRID_PAGESIZE
    // @TODO: find another strategy to prevent useless fetches
    if (visibleRowsCountRounded % EXPLORER_DATAGRID_PAGESIZE !== 0) {
      return;
    }*/

    apiRef.current?.setLoading(true);

    if (playlistId === EXPLORER_ROOT) {
      fetchRootExplorerItems({
        bandWorkspaceId: bandWorkspace?.id,
        offset: visibleRowsCountRounded,
        limit: EXPLORER_DATAGRID_PAGESIZE,
      })
        .then(items => {
          appendItems(items)
        })
        .finally(() => {
          apiRef?.current?.setLoading(false)
        })
        .catch((e) => {
          console.log(e)
          apiRef?.current?.setLoading(false)
        })

      return;
    }

    fetchExplorerItems({
      folderId: playlistId,
      offset: visibleRowsCountRounded,
      limit: EXPLORER_DATAGRID_PAGESIZE,
      bandWorkspaceId: bandWorkspace?.id,
    })
      .then(items => {
        appendItems(items)
      })
      .finally(() => {
        apiRef?.current?.setLoading(false)
      })
      .catch((e) => {
        console.log(e)
        apiRef?.current?.setLoading(false)
      })
  }, [
    apiRef,
    appendItems,
    bandWorkspace?.id,
    playlistId
  ])

  /*const visibleRows = useMemo(() => {
    const rows = explorerItems ? Object.values(explorerItems) : [];

    if (qSearchParam && searchResults) {
      return Object.values(searchResults)
    }

    return rows.filter(row => {
      if (row.deletedAt) {
        return false;
      }

      return !row.hidden
        && row.explorerItemParentId === playlistId;
    });
  }, [
    explorerItems,
    playlistId,
    qSearchParam,
    searchResults
  ]);*/

  const fetchSearch = useCallback(({ searchTerm }: { searchTerm: string }) => {
    setQSearchParam(searchTerm);
    console.log('ExplorerContent.fetchSearch!');
    setIsSearching(true)
    fetchSearchExplorerItems(searchTerm)
      .then((result: ExplorerItems) => {
        setPlaymodeQueryString(PlaymodeType.SEARCH_RESULT)
        setSearchResults(result);
        setQSearchParam(searchTerm);
        appendItems(result);
        setIsSearching(false)
      })
  }, [
    setQSearchParam,
    setPlaymodeQueryString,
    setSearchResults,
    appendItems
  ])

  useEffect(() => {
    Emitter.on(EventTypes.SEARCH, fetchSearch)

    return () => {
      Emitter.off(EventTypes.SEARCH, fetchSearch)
    }
  }, [
    fetchSearch
  ])

  const {
    cellModesModel,
    handleCellClick,
    handleCellEditStart,
    handleCellEditStop,
  } = useCellEditMode()
  const {
    isDragging,
    handleDragMove,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    draggedItemId,
  } = useSortableApi(apiRef.current?.getSortedRows() as ExplorerItem[]);

  const firstRow = useMemo(
    () => apiRef.current?.getSortedRows()[0],
    [apiRef]
  );

  useEffect(() => {
    if (!apiRef.current) {
      return;
    }

    setDataGridApi(apiRef);
  }, [
    apiRef,
    setDataGridApi
  ]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent | KeyboardEvent) => {
    if (userIsWriting()) {
      return;
    }

    if ((event.ctrlKey || event.metaKey) && (event.key === " " || event.keyCode === 32)) {
      event.preventDefault();
      event.stopPropagation();
      emitEvent(EventTypes.FOCUS_SEARCH)
      return
    }

    if (event.key === ' ' || event.keyCode === 32) {
      event.preventDefault();
      event.stopPropagation();
      togglePlay();
    }
    const rowSelectionManager = gridRowSelectionManagerSelector(apiRef);
    const selectedRows = rowSelectionManager.data;
    const resetSelection = !event.shiftKey;

    // select next row
    if (!event.shiftKey && !event.altKey && event.key === 'ArrowDown') {
      const item = (selectedRows.size > 1
        // find last row in selected items
        ? Array.from(selectedRows.keys())[selectedRows.size - 1]
        // get the single selected row
        : selectedRows.keys().next()?.value) || firstRow;

      const index = apiRef.current?.getRowIndexRelativeToVisibleRows(item as GridRowId) || 0
      const nextIndex = index + 1;

      const id = apiRef?.current?.getRowIdFromRowIndex(nextIndex)
      if (!id) {
        return
      }

      apiRef?.current?.selectRow(
        id,
        true,
        resetSelection,
      )

      return;
    }

    // open or play current row (we have only folders or audio rows right now)
    if (event.key === 'Enter' || (!event.altKey && !event.shiftKey && event.metaKey && event.key === 'ArrowDown')) {
      const selectedItem = selectedRows.values().next()?.value as unknown as ExplorerItem;

      if (selectedItem.type === ExplorerItemType.TRACK) {
        play(selectedItem)
      } else {
        setPlaylistId(selectedItem.id)
      }

      return;
    }

    // select previous row
    if (!event.shiftKey && !event.altKey && event.key === 'ArrowUp') {
      const entry = selectedRows.entries().next().value
      if (!entry) {
        return;
      }

      const item = (entry[1] || firstRow) as ExplorerItem;
      const index = apiRef.current?.getRowIndexRelativeToVisibleRows(item.id) || 0

      // @TODO: navigate one folder up
      /*if (event.metaKey && first && last?.type === ExplorerItemType.PLAYLIST) {
        setPlaylistId(last.explorerItemParentId)

        return;
      }*/

      const nextIndex = index - 1;
      const id = apiRef?.current?.getRowIdFromRowIndex(nextIndex)
      if (!id) {
        return
      }

      apiRef?.current?.selectRow(
        id,
        true,
        resetSelection,
      )

      return
    }
  }, [
    userIsWriting,
    apiRef,
    togglePlay,
    firstRow,
    play,
    setPlaylistId
  ]);

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    setExplorerDataGridSortingModel(model)
  }, [setExplorerDataGridSortingModel])

  const handleColumnVisibilityModelChange = useCallback((model: GridColumnVisibilityModel) => {
    setExplorerDataGridColumnVisibilityModel(model)
  }, [setExplorerDataGridColumnVisibilityModel])

  const handleProcessRowUpdate = useCallback((updatedRow: ExplorerItem/*, originalRow: ExplorerItem*/) => {
    if (updatedRow.type === ExplorerItemType.TRACK) {
      renameTrack(updatedRow.track.id, updatedRow.name);
    }

    if (updatedRow.type === ExplorerItemType.PLAYLIST) {
      renamePlaylist(updatedRow.id, updatedRow.name, uniqueTabId);
    }

    updateItem(updatedRow)

    return updatedRow
  }, [
    uniqueTabId,
    updateItem
  ])

  // type Customer = (typeof rows)[number];
  /*const getDetailPanelContent = React.useCallback<
    NonNullable<DataGridProProps['getDetailPanelContent']>
  >(({ row }) => <Waveform selected={false} explorerItemId={currentExplorerItemId || ''} />, []);*/

  /*const autosizeOptions = useMemo(() => ({
    columns: ['name'],
    outliersFactor: 1,
    expand: DEFAULT_GRID_AUTOSIZE_OPTIONS.expand,
    includeHeaders: DEFAULT_GRID_AUTOSIZE_OPTIONS.includeHeaders,
    includeOutliers: DEFAULT_GRID_AUTOSIZE_OPTIONS.includeOutliers,
  }), []);*/

  const slots = useMemo(() => ({
    row: (props: GridRowProps) => {
      return (
        <Sortable
          id={props.row.id}
          disabled={!isDefaultSorting || !!qSearchParam}
        >
          {props.row.type === ExplorerItemType.TRACK && <ExplorerItemRowTrack {...props} />}
          {props.row.type === ExplorerItemType.PLAYLIST && <ExplorerItemRowPlaylist {...props} />}
        </Sortable>
      )
    },
  }), [
    isDefaultSorting,
    qSearchParam,
  ]);

  const handleRowDoubleClick = useCallback((params: GridRowParams) => {
    if (userIsWriting()) return;

    if (params.row.type === ExplorerItemType.TRACK) {
      if (params.row.id === currentExplorerItemId) {
        replay();
      } else {
        play(params.row)
      }
      return;
    }

    if (params.row.type === ExplorerItemType.PLAYLIST) {
      setQSearchParam('');
      setPlaylistId(params.id as ExplorerItemId);
    }
  }, [
    userIsWriting,
    currentExplorerItemId,
    replay,
    play,
    setQSearchParam,
    setPlaylistId
  ]);

  const isNonRootFolder = useMemo(() => {
    return playlistId !== EXPLORER_ROOT
  }, [
    playlistId,
  ])

  const handleGoUp = () => {
    const currentItem = explorerBreadcrumbs.find(
      item => item.id === playlistId
    );

    const parentId = currentItem?.explorerItemParentId || EXPLORER_ROOT;

    if (parentId === EXPLORER_ROOT) {
      setPlaylistId(null)
    } else {
      setPlaylistId(parentId)
    }
  };

  return (
    <DndContext
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      onDragCancel={handleDragCancel}
      onDragMove={handleDragMove}
    >
      {
        !isDragging && !isMobile && <SelectoTracks explorerFolderId={playlistId as string} />
      }
      <SortableContext
        items={rowIds}
        strategy={verticalListSortingStrategy}
        disabled={!isDefaultSorting}
      >
        <div
          onKeyDownCapture={(e: React.KeyboardEvent) => {
            handleKeyDown(e)
          }}
          className={`virtual-list-${playlistId}`}
          style={{
            height: '100%',
            minWidth: "100%"
          }}
        >
          {
            isInExplorer && isNonRootFolder && <FlexBox
              sx={{
                zIndex: 1,
                mx: 1,
                left: 1,
                top: 7,
                position: 'absolute',
              }}
            >
              <DroppableFolderUpButton
                onGoUp={handleGoUp}
                tooltip="Go one folder up"
              />
            </FlexBox>
          }
          <ExplorerToolsMemo />
          <DataGridPro
            apiRef={apiRef}

            onRowsScrollEnd={handleRowsScrollEnd}
            rows={visibleRows}

            slots={slots}
            slotProps={explorerContentSlotProps}
            density={'standard'}
            lazyLoading
            paginationModel={{ page: 0, pageSize: EXPLORER_DATAGRID_PAGESIZE }}
            loading={isSearching}
            columns={ExplorerContentColumns()}
            cellModesModel={cellModesModel}
            onCellClick={handleCellClick}
            onCellEditStop={handleCellEditStop}
            onCellEditStart={handleCellEditStart}
            processRowUpdate={handleProcessRowUpdate}
            onProcessRowUpdateError={(error) => console.error('Error updating row:', error)}
            keepColumnPositionIfDraggedOutside={true}
            onRowDoubleClick={handleRowDoubleClick}
            localeText={{
              toolbarColumns: isMediumScreen ? '' : 'Columns',
            }}
            onSortModelChange={handleSortModelChange}
            onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
            initialState={{
              scroll: getInitialScrollState(),
              sorting: {
                sortModel: explorerDataGridSortingModel,
              },
              columns: {
                columnVisibilityModel: explorerDataGridColumnVisibilityModel,
              }
            }}
          />
        </div>
      </SortableContext>
      {
        apiRef.current?.state
        && isDefaultSorting
        && apiRef.current.getSelectedRows()?.size > 1
        && (
          <DraggableBadge
            selectedRowCount={apiRef.current.getSelectedRows().size}
            isDragging={isDragging}
          />
        )
      }
      {
        isDragging && draggedItemId && createPortal(
          <DraggableOverlay>
            <span>{explorerItems[draggedItemId]?.name}</span>
          </DraggableOverlay>,
          document.body
        )
      }
    </DndContext>
  );
};
